import React, { useEffect, useMemo, useState } from "react";
import { DocumentPreviewUI, normalizeDocumentSources } from "../../components/document-preview";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  callApproveLegacyLifeBillRequest,
  callGetLegacyLifeBillRequestById,
  callRejectLegacyLifeBillRequest,
} from "../../api/legacy-life-entry/service";
import "./legacy-life-entry.css";

const money = value => `${Number(value || 0).toLocaleString()} LAK`;
const show = value => (value === null || value === undefined || value === "" ? "-" : value);
const formatDate = value => {
  if (!value || value === "-") return "-";
  const raw = String(value);
  const d = new Date(raw.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(d.getTime())) return raw;
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
};

export default function LegacyLifeBillApprove() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const fromList = Boolean(location.state?.fromList);
  const fromApproval = Boolean(location.state?.approval);
  const { id } = useParams();
  const [row, setRow] = useState(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    dispatch(callGetLegacyLifeBillRequestById(id)).then(rsp => {
      if (rsp?.payload?.responseCode === "00000") setRow(rsp.payload.data);
    });
  }, [dispatch, id]);

  const summary = useMemo(() => {
    const periods = row?.periods || [];
    const gross = Number(row?.grossAmount ?? periods.reduce((s, x) => s + Number(x.payPerMonth || 0), 0));
    const discount = Number(row?.promotionDiscount || 0);
    const net = Number(row?.netInsuranceAmount ?? Math.max(0, gross - discount));
    return { gross, discount, net };
  }, [row]);

  if (!row) return null;
  const documents = normalizeDocumentSources(
    row.documents ?? row.documentsJson ?? row.documents_json ?? row.documentPath ?? row.document_path ?? row.attachments ?? []
  );
  const periods = row.periods || [];
  const promotionCode = row.promotionCode || row.promotion_code || "NONE";
  const contractNumber = row.contractNumber || row.contract_number || "-";
  const billNumber = row.billNumber || row.bill_number || "-";
  const paidDate = row.paidDate || row.paid_date || "-";
  const monthCount = row.monthCount ?? row.month_count ?? periods.length ?? 0;

  const approve = async () => {
    const rsp = await dispatch(callApproveLegacyLifeBillRequest(id));
    if (rsp?.payload?.responseCode === "00000") navigate("/admin/legacyLifeEntry/approval");
  };
  const reject = async () => {
    if (!reason.trim()) return;
    const rsp = await dispatch(callRejectLegacyLifeBillRequest({ id, reason: reason.trim() }));
    if (rsp?.payload?.responseCode === "00000") navigate("/admin/legacyLifeEntry/approval");
  };

  return <div className="legacy-wrap legacy-bill-approval-page legacy-bill-detail-v2">
    {fromList && <div className="legacy-detail-nav"><button type="button" className="legacy-back-button" onClick={() => navigate(fromApproval ? "/admin/legacyLifeEntry/approval" : "/admin/legacyLifeEntry/list")}>← {t("legacyLifeEntry.back")}</button><span>{t("legacyLifeEntry.billNumber")}: <strong>{billNumber}</strong></span></div>}
    <div className="legacy-approval-hero legacy-bill-detail-hero">
      <div>
        <div className="legacy-approval-eyebrow">{t("legacyLifeEntry.billApprovalTitle")}</div>
        <h3>{contractNumber}</h3>
        <p>{t("legacyLifeEntry.billNumber")} <strong>{billNumber}</strong></p>
      </div>
      <span className={`legacy-badge ${row.status}`}>{t(`legacyLifeEntry.statusText.${row.status}`)}</span>
    </div>

    <div className="legacy-approval-summary-strip">
      <div><span>{t("legacyLifeEntry.monthCount")}</span><strong>{monthCount}</strong></div>
      <div><span>{t("legacyLifeEntry.totalBeforeDiscount")}</span><strong>{money(summary.gross)}</strong></div>
      <div className="discount"><span>{t("legacyLifeEntry.promotionDiscount")}</span><strong>- {money(summary.discount)}</strong></div>
      <div className="net"><span>{t("legacyLifeEntry.netInsuranceAmount")}</span><strong>{money(summary.net)}</strong></div>
    </div>

    <div className="legacy-approve-grid legacy-approve-grid-modern">
      <section className="legacy-info-card legacy-approval-card">
        <div className="legacy-card-title"><span>01</span><h4>{t("legacyLifeEntry.billInfo")}</h4></div>
        <div className="legacy-info-list legacy-bill-detail-list">
          <div className="legacy-info-item"><span>{t("legacyLifeEntry.contractNumber")}</span><strong>{show(contractNumber)}</strong></div>
          <div className="legacy-info-item"><span>{t("legacyLifeEntry.billNumber")}</span><strong>{show(billNumber)}</strong></div>
          <div className="legacy-info-item"><span>{t("legacyLifeEntry.customerName")}</span><strong>{show(row.customerName)}</strong></div>
          <div className="legacy-info-item"><span>{t("legacyLifeEntry.paidDate")}</span><strong>{formatDate(paidDate)}</strong></div>
          <div className="legacy-info-item"><span>{t("legacyLifeEntry.previousPaidDate")}</span><strong>{formatDate(row.previousPaidDate || row.previous_paid_date)}</strong></div>
          <div className="legacy-info-item"><span>{t("legacyLifeEntry.previousMonthCount")}</span><strong>{Number(row.previousMonthCount ?? row.previous_month_count ?? 0)} {t("legacyLifeEntry.months")}</strong></div>
          <div className="legacy-info-item"><span>{t("legacyLifeEntry.previousBillNumber")}</span><strong>{row.previousBillNumber || row.previous_bill_number || "-"}</strong></div>
          <div className="legacy-info-item legacy-promotion-value"><span>{t("legacyLifeEntry.promotionTitle")}</span><strong>{t(`legacyLifeEntry.promotionCode.${promotionCode}`)}</strong></div>
        </div>
      </section>

      <section className="legacy-info-card legacy-approval-card">
        <div className="legacy-card-title"><span>02</span><h4>{t("legacyLifeEntry.agentSection")}</h4></div>
        <div className="legacy-agent-profile">
          <div className="legacy-agent-avatar">{String(row.agentName || row.agentCode || "A").charAt(0).toUpperCase()}</div>
          <div><strong>{row.agentName || "-"}</strong><small>{row.agentCode || row.agentId || "-"}</small></div>
        </div>
      </section>

      <section className="legacy-span2 legacy-approval-card">
        <div className="legacy-card-title"><span>03</span><h4>{t("legacyLifeEntry.generatedPeriods")}</h4></div>
        <div className="legacy-period-payment-grid">
          {periods.map((item, index) => <div className={`legacy-period-payment${item.isFree ? " free" : ""}`} key={`${item.paidYear}-${item.paidMonth}`}>
            <span>{String(item.paidMonth).padStart(2,"0")}/{item.paidYear}</span>
            <strong>{item.isFree ? '': money(item.netAmount ?? item.payPerMonth)}</strong>
            {item.isFree && <small>{t("legacyLifeEntry.freeMonth")}</small>}
          </div>)}
        </div>
      </section>

      <section className="legacy-span2 legacy-approval-card">
        <div className="legacy-card-title"><span>04</span><h4>{t("legacyLifeEntry.billDocuments")}</h4></div>
        <div className="legacy-docs legacy-docs-modern">{documents.length ? <DocumentPreviewUI documents={documents} variant="cell" /> : <span>-</span>}</div>
      </section>
    </div>

    {row.status === "REJECTED" && (row.rejectReason || row.reject_reason) && <div className="legacy-reject-notice"><div><strong>{t("legacyLifeEntry.rejectedComment")}</strong><p>{row.rejectReason || row.reject_reason}</p></div></div>}

    {row.status === "PENDING" && <div className="legacy-approval-actions legacy-approval-actions-modern">
      <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder={t("legacyLifeEntry.rejectReason")} />
      <div><button className="reject" onClick={reject}>{t("legacyLifeEntry.reject")}</button><button className="approve" onClick={approve}>{t("legacyLifeEntry.approve")}</button></div>
    </div>}
  </div>;
}
